import React, { useState } from 'react'
import styled from 'styled'

import { Button } from 'components/ui/button'
import { Content, Container, Wrap, DragHandle } from 'components/ui/draggable'
import { IconWebpage, IconPage, IconArrow, IconPlus, IconParagraph, IconDivider } from 'components/ui/icons'
import { Title } from 'components/ui/list-item-label'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'

import { ReorderableAmenities } from './reorderable-amenities'

import { space } from 'helpers/style'

import { VisitorInfoPage, Amenity } from 'generated/mos/org/getty/visitorinfo'

import { visitorInfoSchema } from '../../schemas/visitorInfo'

const Amenities = styled.div`
  margin-top: ${space(4)};
  margin-left: ${space(8)};
`

const Arrow = styled(IconArrow)<{ open: boolean }>`
  transform: rotate(${({ open }) => !open ? '-90' : '0'}deg);
  margin-right: ${space(3)};
`

const Icon = (type: string) => {
  const style = `
    margin-right: ${space(3)};
  `
  switch(type) {
    case VisitorInfoPage.SectionSeparator.refName :
      return styled(IconDivider)`${style}`
    case VisitorInfoPage.AmenityCategory.refName :
      return styled(IconWebpage)`${style}`
    case VisitorInfoPage.SiteMap.refName :
    case VisitorInfoPage.StaticWebPage.refName :
    case VisitorInfoPage.URLWebPage.refName :
    case VisitorInfoPage.AppPage.refName :
    case VisitorInfoPage.DeepDivePage.refName :
      return styled(IconPage)`${style}`
    default:
      return styled(IconParagraph)`${style}`
  }
}

type Props = {
  readonly draggableIndex?: number | undefined;
  readonly page: VisitorInfoPage.Entity;
  readonly amenities: ReadonlyArray<Amenity.Entity> | undefined;
  readonly deleteHandler: (info: VisitorInfoPage.Entity) => void;
  readonly updateHandler: (info: VisitorInfoPage.Entity) => void;
  readonly deleteAmenityHandler: (page: VisitorInfoPage.Entity, amenity: Amenity.Entity) => void;
  readonly updateAmenityHandler: (amenity: Amenity.Entity) => void;
  readonly createAmenityHandler: (ref: VisitorInfoPage.Ref) => void;
  readonly isDragging?: boolean;
  readonly setOrderedAmenities: (amenities: ReadonlyArray<Amenity.Entity>) => void;
}

export const VisitorInfoItem = ({ draggableIndex, page, amenities, deleteHandler, updateHandler, isDragging, deleteAmenityHandler, updateAmenityHandler, createAmenityHandler, setOrderedAmenities }: Props) => {
  const [showAmenities, setShowAmenities] = useState<boolean>(true)
  const ItemIcon = Icon(page.pageProperties && page.pageProperties.type || '')
  const editDisabled = page.pageProperties && page.pageProperties.type === VisitorInfoPage.SectionSeparator.refName
  return (
    <Container isDragging={isDragging}>
      <Wrap isDragging={isDragging}>
        {draggableIndex !== undefined && <DragHandle />}
        <Content onClick={() => amenities && amenities.length && setShowAmenities(!showAmenities)}>
          {amenities && <Arrow size="medium" open={showAmenities} />}
          <ItemIcon size="medium" />
          <Title>{visitorInfoSchema.listTitle.read(page.listTitle) || '-'}</Title>
        </Content>
        <PopOverMenu iconSize="base">
          {!editDisabled &&
            <PopOverMenuButton
              onClick={() => updateHandler(page)}
              >
                Edit
            </PopOverMenuButton>
          }
          <PopOverMenuButton
            onClick={() => deleteHandler(page)}
            >
              Delete
          </PopOverMenuButton>
        </PopOverMenu>
      </Wrap>
      {showAmenities && amenities &&
        <Amenities>
          <ReorderableAmenities
            orderedAmenities={amenities}
            setOrderedAmenities={setOrderedAmenities}
            updateHandler={updateAmenityHandler}
            deleteHandler={amenity => deleteAmenityHandler(page, amenity)}
          />
          <Button variant="subtle" onClick={() => createAmenityHandler(VisitorInfoPage.mustRef(page.ref))}>
            <IconPlus size="small" />Add Section
          </Button>
        </Amenities>
      }
    </Container>
  )
}
